// Pure resolution of a failed submit: turns per-slot errors into the pruned
// assignments, locks/highlights to apply, and the error to throw. React-free
// so the decision is unit-testable; the hook just applies the result.
import {
  VolunteerRegistrationErrorCode,
  type VolunteerRegistrationSlotError,
} from '@sola/schema/dotcom/supporters/volunteers.ts';

import {
  type RawSlot,
  type ShiftAssignments,
  type VolunteerContact,
  buildSlotGroupIds,
  getContactDisplayName,
  getSlotGroupKey,
} from '../../../utils/volunteer-utils.ts';
import {
  AggregateConflictError,
  DuplicateInShiftError,
  EventFullError,
  GuestUnresolvedError,
  SameDayConflictError,
  ShiftFullError,
  SlotConflictError,
} from './volunteer-conflict-errors.ts';

// ── Assignment pruning (pure) ────────────────────────────────────────────────

/** A backend-flagged conflicted seat: a contact and the slot that was rejected. */
interface DuplicateSlotConflict {
  contactId: string;
  slotId: string;
}

/**
 * Identify shift groups that can't fit the current selection: groups where the
 * seats the assignments need exceed the seats available once backend-rejected
 * seats are excluded.
 *
 * Distinguishes a retryable slot conflict from a full shift. Example: a group
 * has 2 seats and the user wants 2, but the backend just rejected 1
 * (ALREADY_BOOKED). After excluding it, 1 seat remains for 2 needed — full,
 * can't be remapped. If 3 were open with 1 rejected, 2 remain for 2 needed —
 * still fulfillable, so a retry can recover it.
 *
 * Returns the full groups' keys, in first-seen order.
 */
export const getFullShiftGroups = (
  assignments: ShiftAssignments,
  slots: RawSlot[],
  unavailableSlotIds: ReadonlySet<string> = new Set(),
): string[] => {
  const groupPool = buildSlotGroupIds(slots);
  const slotIdToGroupKey = new Map<string, string>();
  for (const slot of slots) {
    slotIdToGroupKey.set(slot.id, getSlotGroupKey(slot));
  }

  // How many seats each group needs, from the current assignments.
  const neededByGroup = new Map<string, number>();
  for (const slotIds of Object.values(assignments)) {
    for (const slotId of slotIds) {
      const groupKey = slotIdToGroupKey.get(slotId);
      if (!groupKey) {
        continue;
      }
      neededByGroup.set(groupKey, (neededByGroup.get(groupKey) ?? 0) + 1);
    }
  }

  const fullGroupKeys: string[] = [];
  for (const [groupKey, needed] of neededByGroup) {
    const pool = groupPool.get(groupKey) ?? [];
    const available = pool.filter((id) => !unavailableSlotIds.has(id)).length;
    if (needed > available) {
      fullGroupKeys.push(groupKey);
    }
  }

  return fullGroupKeys;
};

/**
 * Remove every assignment a contact holds in a locked shift group. Used when a
 * contact is flagged as already signed up (DUPLICATE_IN_SHIFT) or booked that
 * day (SAME_DAY_CONFLICT): they get locked into that group, so they must also
 * drop off the pending assignment — off the payload, the checkbox, and the
 * confirmation list — regardless of which slot ID they held.
 *
 * `lockedGroupsByContact` maps a contactId to the group keys now locked for
 * them. `slots` resolves each assignment's slotId to its group key. A contact
 * key is deleted once none of their assignments remain.
 *
 * Returns the pruned assignments and the contactIds that had something removed.
 */
export const removeAssignmentsForLockedGroups = (
  assignments: ShiftAssignments,
  slots: RawSlot[],
  lockedGroupsByContact: Record<string, string[]>,
): { assignments: ShiftAssignments; removedContactIds: string[] } => {
  const slotIdToGroupKey = new Map<string, string>();
  for (const slot of slots) {
    slotIdToGroupKey.set(slot.id, getSlotGroupKey(slot));
  }

  const result: ShiftAssignments = {};
  const removedContactIds: string[] = [];

  for (const [contactId, slotIds] of Object.entries(assignments)) {
    const lockedGroups = lockedGroupsByContact[contactId];
    if (!lockedGroups || lockedGroups.length === 0) {
      result[contactId] = [...slotIds];
      continue;
    }
    const lockedSet = new Set(lockedGroups);
    const kept = slotIds.filter((slotId) => {
      const groupKey = slotIdToGroupKey.get(slotId);
      // Drop the slot when its group is locked for this contact. Keep slots we
      // can't resolve (unknown group) rather than silently dropping them.
      return !groupKey || !lockedSet.has(groupKey);
    });

    if (kept.length !== slotIds.length) {
      removedContactIds.push(contactId);
    }
    if (kept.length > 0) {
      result[contactId] = kept;
    }
  }

  return { assignments: result, removedContactIds };
};

/**
 * Remove specific slot IDs from every contact's assignments.
 *
 * Used when the backend rejects individual seats as taken/gone
 * (ALREADY_BOOKED / SLOT_NOT_FOUND) and the group is full, so those seats can't
 * be remapped. Pruning them keeps every downstream view (payload, confirmation,
 * checkboxes) in sync with what the slot cards show.
 *
 * `submittedAssignments` are the fresh (remapped) IDs actually sent, so the
 * rejected IDs match them — not `stateAssignments`, whose IDs may differ after
 * the remap. The remap is position-aligned (index-for-index within each
 * contact), so a rejected slot is located by its index in
 * `submittedAssignments[contactId]` and the same index is dropped from
 * `stateAssignments[contactId]`. A contact key is deleted once none remain.
 *
 * Returns the pruned assignments plus the contactIds that had a slot removed.
 */
export const removeUnavailableAssignments = (
  stateAssignments: ShiftAssignments,
  submittedAssignments: ShiftAssignments,
  unavailableSlotIds: ReadonlySet<string>,
): { assignments: ShiftAssignments; removedContactIds: string[] } => {
  const assignments: ShiftAssignments = {};
  const removedContactIds: string[] = [];

  for (const [contactId, stateSlotIds] of Object.entries(stateAssignments)) {
    const submittedSlotIds = submittedAssignments[contactId] ?? [];
    // Keep a state slot unless the slot submitted at the same index was rejected.
    const kept = stateSlotIds.filter(
      (_slotId, index) => !unavailableSlotIds.has(submittedSlotIds[index]),
    );

    if (kept.length !== stateSlotIds.length) {
      removedContactIds.push(contactId);
    }
    if (kept.length > 0) {
      assignments[contactId] = kept;
    }
  }

  return { assignments, removedContactIds };
};

// ── Submit-response resolution ───────────────────────────────────────────────

export interface ResolveSubmitConflictsInput {
  /** Per-slot errors from the failed response. */
  errors: VolunteerRegistrationSlotError[];
  /** Current reducer assignments (the working copy to prune). */
  stateAssignments: ShiftAssignments;
  /** The remapped assignments actually submitted. */
  freshAssignments: ShiftAssignments;
  /** Live slots after the pre-submit refresh. */
  freshRawSlots: RawSlot[];
  /** Pre-refresh slots, so a consumed ID still resolves to its group. */
  previousRawSlots: RawSlot[];
  /** Seats the backend already rejected this session. */
  unavailableSlotIds: ReadonlySet<string>;
  /** All contacts, for resolving flagged ids to display names. */
  contacts: VolunteerContact[];
}

export interface ResolveSubmitConflictsResult {
  /** Assignments after pruning conflicted seats (same ref when unchanged). */
  nextAssignments: ShiftAssignments;
  /** Seats the backend rejected as taken/gone; add to unavailableSlotIds. */
  rejectedSlotIds: string[];
  /** contactId -> group keys locked as duplicates; merge into conflictRegistrations. */
  duplicateLocks: Record<string, string[]>;
  /** contactId -> group keys locked as same-day; merge into sameDayRegistrations. */
  sameDayLocks: Record<string, string[]>;
  /** Group keys to highlight as full. */
  fullGroupKeys: string[];
  /** The error to throw (single, or AggregateConflictError for a mixed response). */
  error: Error;
}

const conflictsOfCode = (
  errors: VolunteerRegistrationSlotError[],
  code: VolunteerRegistrationErrorCode,
): DuplicateSlotConflict[] =>
  errors
    .filter((error) => error.code === code && error.contactId)
    .map((error) => ({
      contactId: error.contactId as string,
      slotId: error.slotId,
    }));

/** Resolve a flagged slotId to its group across the fresh + pre-refresh slots. */
const buildGroupKeyBySlotId = (slots: RawSlot[]): Map<string, string> => {
  const map = new Map<string, string>();
  for (const slot of slots) {
    if (!map.has(slot.id)) {
      map.set(slot.id, getSlotGroupKey(slot));
    }
  }
  return map;
};

/** Group keys to lock per contact, from flagged (contactId, slotId) conflicts. */
const buildLocks = (
  conflicts: DuplicateSlotConflict[],
  groupKeyBySlotId: Map<string, string>,
): Record<string, string[]> => {
  const locks: Record<string, string[]> = {};
  for (const { contactId, slotId } of conflicts) {
    const groupKey = groupKeyBySlotId.get(slotId);
    if (!groupKey) {
      continue;
    }
    locks[contactId] = [...new Set([...(locks[contactId] ?? []), groupKey])];
  }
  return locks;
};

/**
 * Decide what a failed submit means. Prunes conflicted seats off a single
 * working copy, collects the locks/highlights to apply, and builds the error to
 * throw — a single error, or an AggregateConflictError when a response carries
 * more than one blocking problem.
 */
export const resolveSubmitConflicts = ({
  errors,
  stateAssignments,
  freshAssignments,
  freshRawSlots,
  previousRawSlots,
  unavailableSlotIds,
  contacts,
}: ResolveSubmitConflictsInput): ResolveSubmitConflictsResult => {
  const messages = errors.map((error) => error.message);
  const allSlots = [...freshRawSlots, ...previousRawSlots];
  const groupKeyBySlotId = buildGroupKeyBySlotId(allSlots);

  const nameFor = (contactId: string): string => {
    const contact = contacts.find((c) => c.id === contactId);
    return contact
      ? getContactDisplayName(contact, { variant: 'full' })
      : contactId;
  };

  const isCode = (...codes: VolunteerRegistrationErrorCode[]) =>
    errors.some((error) =>
      codes.includes(error.code as VolunteerRegistrationErrorCode),
    );
  const hasDuplicateInShift = isCode(
    VolunteerRegistrationErrorCode.DuplicateInShift,
  );
  const hasSlotConflict = isCode(
    VolunteerRegistrationErrorCode.AlreadyBooked,
    VolunteerRegistrationErrorCode.SlotNotFound,
  );
  const hasSameDayConflict = isCode(
    VolunteerRegistrationErrorCode.SameDayConflict,
  );
  const hasGuestUnresolved = isCode(
    VolunteerRegistrationErrorCode.GuestUnresolved,
  );

  // Seats the backend rejected as taken/gone — excluded from remap + capacity.
  const rejectedSlotIds = errors
    .filter((error) =>
      [
        VolunteerRegistrationErrorCode.AlreadyBooked,
        VolunteerRegistrationErrorCode.SlotNotFound,
      ].includes(error.code as VolunteerRegistrationErrorCode),
    )
    .map((error) => error.slotId);

  let nextAssignments = stateAssignments;
  const fullGroupKeysToHighlight = new Set<string>();

  // Event full: no Available seat remains once rejected seats are excluded.
  // Outranks every per-shift conflict below — nothing to remap or pick.
  const excludedSlotIds = new Set([...unavailableSlotIds, ...rejectedSlotIds]);
  const eventFull = !freshRawSlots.some(
    (slot) => slot.status === 'Available' && !excludedSlotIds.has(slot.id),
  );

  // Lock each flagged contact into its conflicting group and drop that seat, so
  // the contact leaves the payload, checkbox, and confirmation list. Shared by
  // duplicate and same-day — same remediation, different message. Returns the
  // locks, the handled names (review), and the unmappable names (double-check).
  const resolveLockingConflicts = (conflicts: DuplicateSlotConflict[]) => {
    const locks = buildLocks(conflicts, groupKeyBySlotId);
    const removedNames: string[] = [];
    const unresolvedNames: string[] = [];

    // Every mapped contact is handled (locked + pruned below), so report them
    // in the review bucket rather than the manual "remove them" message.
    for (const contactId of Object.keys(locks)) {
      const name = nameFor(contactId);
      if (!removedNames.includes(name)) {
        removedNames.push(name);
      }
    }

    // Flagged contacts we couldn't map to a group (e.g. unmapped guest).
    const handled = new Set(Object.keys(locks));
    for (const { contactId } of conflicts) {
      if (!handled.has(contactId)) {
        const name = nameFor(contactId);
        if (!unresolvedNames.includes(name)) {
          unresolvedNames.push(name);
        }
      }
    }

    return { locks, removedNames, unresolvedNames };
  };

  const duplicate = resolveLockingConflicts(
    conflictsOfCode(errors, VolunteerRegistrationErrorCode.DuplicateInShift),
  );
  const sameDay = resolveLockingConflicts(
    conflictsOfCode(errors, VolunteerRegistrationErrorCode.SameDayConflict),
  );

  // Slot conflict: decide retry vs. full. A group over capacity once rejected
  // seats are excluded can't be remapped (getFullShiftGroups). Additionally, a
  // rejected shift whose group has no available seat left in the feed isn't
  // retryable either — treat it as full so the user bounces back to reselect
  // rather than resubmitting a shift that's gone.
  let hasFullShift = false;
  if (hasSlotConflict) {
    const exclusions = new Set([...unavailableSlotIds, ...rejectedSlotIds]);
    const availableGroupKeys = new Set(
      freshRawSlots
        .filter((s) => s.status === 'Available' && !exclusions.has(s.id))
        .map((s) => getSlotGroupKey(s)),
    );
    const unavailableRejectedGroups = rejectedSlotIds
      .map((id) => groupKeyBySlotId.get(id))
      .filter((key): key is string => !!key && !availableGroupKeys.has(key));

    const fullGroupKeys = [
      ...new Set([
        ...getFullShiftGroups(freshAssignments, freshRawSlots, exclusions),
        ...unavailableRejectedGroups,
      ]),
    ];
    if (fullGroupKeys.length > 0) {
      hasFullShift = true;
      for (const key of fullGroupKeys) {
        fullGroupKeysToHighlight.add(key);
      }
      // Prune off the ORIGINAL state so index alignment with freshAssignments
      // holds. The locked-group prune (position-independent) runs after.
      const { assignments, removedContactIds } = removeUnavailableAssignments(
        stateAssignments,
        freshAssignments,
        new Set(rejectedSlotIds),
      );
      if (removedContactIds.length > 0) {
        nextAssignments = assignments;
      }
    }
  }

  // Locked-group prune runs last: it filters by group key (position-independent)
  // so it's safe on the already-shortened unavailable-pruned map above. Merge
  // duplicate + same-day locks per contact (a contact may have both).
  const allLocks: Record<string, string[]> = {};
  for (const [contactId, groupKeys] of [
    ...Object.entries(duplicate.locks),
    ...Object.entries(sameDay.locks),
  ]) {
    allLocks[contactId] = [
      ...new Set([...(allLocks[contactId] ?? []), ...groupKeys]),
    ];
  }
  if (Object.keys(allLocks).length > 0) {
    const { assignments, removedContactIds } = removeAssignmentsForLockedGroups(
      nextAssignments,
      allSlots,
      allLocks,
    );
    if (removedContactIds.length > 0) {
      nextAssignments = assignments;
    }
  }

  // Collect every blocking error so a mixed response surfaces each message, not
  // just the highest-priority one. Order sets display priority.
  const blockingErrors: Error[] = [];
  if (hasGuestUnresolved) {
    blockingErrors.push(new GuestUnresolvedError(messages));
  }
  if (hasSameDayConflict) {
    blockingErrors.push(
      new SameDayConflictError(
        messages,
        sameDay.removedNames,
        sameDay.unresolvedNames,
      ),
    );
  }
  // The duplicate carries the full-shift line (hadFullShiftRemoval) when a seat
  // was pruned as full this submit, so it isn't double-reported below.
  if (hasDuplicateInShift) {
    blockingErrors.push(
      new DuplicateInShiftError(
        messages,
        duplicate.removedNames,
        duplicate.unresolvedNames,
        hasFullShift,
      ),
    );
  }
  // A full shift with no duplicate to carry it needs its own message.
  if (hasFullShift && !hasDuplicateInShift) {
    blockingErrors.push(
      new ShiftFullError(
        [...fullGroupKeysToHighlight].map(
          (key) => `No available slots left in group "${key}".`,
        ),
        [...fullGroupKeysToHighlight],
      ),
    );
  }

  // Event-full outranks every per-shift message; otherwise a mixed response
  // aggregates, a single blocking error throws directly, and nothing blocking
  // means a remappable slot conflict.
  const pickError = (): Error => {
    if (eventFull) {
      return new EventFullError(messages);
    }
    if (blockingErrors.length > 1) {
      return new AggregateConflictError(blockingErrors);
    }
    return blockingErrors[0] ?? new SlotConflictError(messages);
  };
  const error = pickError();

  return {
    nextAssignments,
    rejectedSlotIds,
    duplicateLocks: duplicate.locks,
    sameDayLocks: sameDay.locks,
    fullGroupKeys: [...fullGroupKeysToHighlight],
    error,
  };
};
